/**
 * DMCA CLAIM FORM
 * 
 * Public form for photographers/swatchers to submit DMCA claims.
 * No authentication required - anyone can submit a claim.
 * 
 * Route: /dmca or /dmca-claim
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Shield,
  Camera,
  Mail,
  Link as LinkIcon,
  AlertTriangle,
  CheckCircle,
  Send,
  ArrowLeft,
  Instagram,
  Globe,
  FileText,
  HelpCircle
} from 'lucide-react';
import { submitDMCAClaim } from '../services/dmcaService';
import type { DMCAClaimFormData } from '../types/dmca';
import { PageHeader } from '../components/common';

export default function DMCAClaimForm() {
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState<DMCAClaimFormData>({
    claimantName: '',
    claimantEmail: '',
    claimantUrl: '',
    photoUrl: '',
    originalPhotoUrl: '',
    description: '',
    requestedAction: 'credit',
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill form from URL parameters (when clicking "Report Photo" from item detail)
  useEffect(() => {
    const photoUrlParam = searchParams.get('photoUrl');
    const pageUrlParam = searchParams.get('pageUrl');
    
    if (photoUrlParam || pageUrlParam) {
      setFormData(prev => ({
        ...prev,
        // If we have a direct photo URL from Firebase Storage, use that
        // Otherwise use the page URL where the photo was found
        photoUrl: photoUrlParam || pageUrlParam || '',
        // Add the page URL to description for context
        description: pageUrlParam 
          ? `Photo found at: ${pageUrlParam}\n\n` 
          : ''
      }));
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (!formData.claimantName.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!formData.claimantEmail.trim() || !formData.claimantEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    if (!formData.photoUrl.trim()) {
      setError('Please enter the URL of the photo in question');
      return;
    }
    if (!formData.description.trim()) {
      setError('Please describe your claim');
      return;
    }

    setSubmitting(true);

    try {
      // Sanitize form data - Firebase doesn't accept undefined values
      const sanitizedData: DMCAClaimFormData = {
        claimantName: formData.claimantName || '',
        claimantEmail: formData.claimantEmail || '',
        claimantUrl: formData.claimantUrl || '',
        photoUrl: formData.photoUrl || '',
        originalPhotoUrl: formData.originalPhotoUrl || '',
        description: formData.description || '',
        requestedAction: formData.requestedAction || 'credit',
      };
      
      await submitDMCAClaim(sanitizedData);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting claim:', err);
      setError('Failed to submit claim. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen py-12 px-4">
        <div className="max-w-xl mx-auto">
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-glass mb-2 font-heading">Claim Submitted</h1>
            <p className="text-glass-secondary mb-6 font-body">
              Thank you for reaching out. We take photo attribution seriously and will review your claim within 48 hours.
            </p>
            <p className="text-sm text-glass-muted mb-6 font-body">
              You'll receive an email at <strong className="text-glass">{formData.claimantEmail}</strong> once we've reviewed your claim.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 btn-primary"
            >
              <ArrowLeft className="w-4 h-4" />
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <PageHeader
          icon={Shield}
          title="Photo Attribution Request"
          subtitle="We respect photographers and swatchers. If your photo is being used without proper credit, let us know and we'll make it right."
          className="mb-6"
        />

        {/* Info Box */}
        <div className="alert-glass-purple mb-6">
          <div className="flex gap-3">
            <HelpCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium mb-1 text-purple-300">What happens next?</p>
              <ul className="list-disc list-inside space-y-1 text-purple-200/80">
                <li>We'll review your claim within 48 hours</li>
                <li>If valid, we'll add proper credit or remove the photo</li>
                <li>You'll be notified by email of the resolution</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="glass-card p-6 md:p-8 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="alert-glass-red flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {/* Your Information Section */}
          <div>
            <h2 className="text-lg font-semibold text-glass mb-4 flex items-center gap-2 font-body">
              <Camera className="w-5 h-5 text-purple-400" />
              Your Information
            </h2>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
                  Your Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.claimantName}
                  onChange={(e) => setFormData(prev => ({ ...prev, claimantName: e.target.value }))}
                  placeholder="Jane Smith"
                  className="input-glass"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
                  Email Address <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-glass-muted" />
                  <input
                    type="email"
                    value={formData.claimantEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, claimantEmail: e.target.value }))}
                    placeholder="you@example.com"
                    className="input-glass pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
                Your Profile/Website
                <span className="text-glass-muted font-normal ml-1">(optional)</span>
              </label>
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-glass-muted" />
                <input
                  type="url"
                  value={formData.claimantUrl}
                  onChange={(e) => setFormData(prev => ({ ...prev, claimantUrl: e.target.value }))}
                  placeholder="https://instagram.com/yourhandle"
                  className="input-glass pl-10"
                />
              </div>
              <p className="text-xs text-glass-muted mt-1 font-body">
                This helps us verify you're the original creator and link proper credit
              </p>
            </div>
          </div>

          {/* Photo Information Section */}
          <div className="pt-4 border-t border-slate-600">
            <h2 className="text-lg font-semibold text-glass mb-4 flex items-center gap-2 font-body">
              <LinkIcon className="w-5 h-5 text-purple-400" />
              Photo Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
                  URL of the photo on our site <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-glass-muted" />
                  <input
                    type="url"
                    value={formData.photoUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, photoUrl: e.target.value }))}
                    placeholder="https://polishallthethings.com/... or the image URL"
                    className="input-glass pl-10"
                  />
                </div>
                <p className="text-xs text-glass-muted mt-1 font-body">
                  Right-click the image and select "Copy image address" to get this URL
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
                  URL to your original photo
                  <span className="text-glass-muted font-normal ml-1">(optional but helpful)</span>
                </label>
                <div className="relative">
                  <Camera className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-glass-muted" />
                  <input
                    type="url"
                    value={formData.originalPhotoUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, originalPhotoUrl: e.target.value }))}
                    placeholder="https://instagram.com/p/... or link to your original"
                    className="input-glass pl-10"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Claim Details Section */}
          <div className="pt-4 border-t border-slate-600">
            <h2 className="text-lg font-semibold text-glass mb-4 flex items-center gap-2 font-body">
              <FileText className="w-5 h-5 text-purple-400" />
              Claim Details
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
                  What would you like us to do? <span className="text-red-400">*</span>
                </label>
                <div className="grid gap-3 md:grid-cols-3">
                  <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all font-body ${
                    formData.requestedAction === 'credit'
                      ? 'border-purple-500 bg-purple-500/20 text-glass'
                      : 'border-slate-600 bg-slate-800/40 text-glass-secondary hover:border-slate-500'
                  }`}>
                    <input
                      type="radio"
                      name="requestedAction"
                      value="credit"
                      checked={formData.requestedAction === 'credit'}
                      onChange={() => setFormData(prev => ({ ...prev, requestedAction: 'credit' }))}
                      className="text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium">Add Credit</span>
                  </label>

                  <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all font-body ${
                    formData.requestedAction === 'remove'
                      ? 'border-purple-500 bg-purple-500/20 text-glass'
                      : 'border-slate-600 bg-slate-800/40 text-glass-secondary hover:border-slate-500'
                  }`}>
                    <input
                      type="radio"
                      name="requestedAction"
                      value="remove"
                      checked={formData.requestedAction === 'remove'}
                      onChange={() => setFormData(prev => ({ ...prev, requestedAction: 'remove' }))}
                      className="text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium">Remove Photo</span>
                  </label>

                  <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all font-body ${
                    formData.requestedAction === 'both'
                      ? 'border-purple-500 bg-purple-500/20 text-glass'
                      : 'border-slate-600 bg-slate-800/40 text-glass-secondary hover:border-slate-500'
                  }`}>
                    <input
                      type="radio"
                      name="requestedAction"
                      value="both"
                      checked={formData.requestedAction === 'both'}
                      onChange={() => setFormData(prev => ({ ...prev, requestedAction: 'both' }))}
                      className="text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium">Either Works</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">
                  Additional Details <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Please tell us more about this photo. When did you take it? Where did you originally post it? Any additional context helps us resolve this quickly."
                  rows={4}
                  className="textarea-glass"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-4 border-t border-slate-600">
            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white rounded-full font-bold text-lg hover:from-fuchsia-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg font-body"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Claim
                </>
              )}
            </button>

            <p className="text-xs text-glass-muted text-center mt-3 font-body">
              By submitting, you confirm that you are the original creator or authorized representative of this photo.
            </p>
          </div>
        </form>

        {/* Footer Link */}
        <div className="text-center mt-6">
          <Link to="/" className="text-on-bg text-sm font-body">
            ← Back to Polish ALL the Things
          </Link>
        </div>
      </div>
    </div>
  );
}
